import * as React from 'react';

import { getLocalTimeZone, now } from '@internationalized/date';
import {
  Badge,
  Button,
  ConsoleDialog,
  EntityDetails,
  isDateWithinHours,
  Loading,
  LocalizedDate,
  LocalizedTime,
  Menu,
  MenuItem,
  Row,
  TextField,
  useToast,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideArchive,
  LucideArchiveRestore,
  LucideDownload,
  LucideEllipsis,
  LucideFileDown,
  LucideFiles,
  LucideGlobe,
  LucideHistory,
  LucideMail,
  LucidePencil,
  LucideTrash2,
} from 'lucide-react';
import { useLocation } from 'wouter';

import {
  customPriceListAddItemPricingAtom,
  customPriceListBatchUpdateItemPricingsAtom,
  customPriceListDeleteItemPricingAtom,
  customPriceListReorderItemPricingsAtom,
  customPriceListUpdateAtom,
} from '../../_graphql/mutationCustomPriceListLifecycle';
import { generatePriceListDocumentAtom } from '../../_graphql/mutationPriceListDocument';
import * as state from '../../_state/customPriceListDetail.state';
import * as variablesState from '../../_state/variablesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { pricingRoutePaths } from '../../routes';
import {
  CustomPriceListRecord,
  CustomPriceListStatus,
} from '../../types/pricing';
import type {
  AddItemPricingInput,
  BatchSaveItem,
} from '../_shared/hooks/useEditablePricingTable';
import { PriceListEmailDialog } from '../_shared/PriceListEmailDialog';
import { PriceListHistoryDialog } from '../_shared/PriceListHistoryDialog';
import { PriceListView } from '../_shared/PriceListView';

import { DuplicateCustomPriceListDialog } from './DuplicateCustomPriceListDialog';
import { useDuplicateCustomPriceList } from './hooks/useDuplicateCustomPriceList';
import { ImportFromPriceListDialog } from './ImportFromPriceListDialog';

import { useFileDownload } from '~/shared/hooks/useFileDownload';

type CustomPriceListTab = 'pricings';

const computeStatus = (list: CustomPriceListRecord): CustomPriceListStatus => {
  if (list.archivedAt) return 'Archived';
  if (!list.isPublished) return 'Draft';
  if (list.endDate && list.endDate.compare(now(getLocalTimeZone())) < 0)
    return 'Expired';
  return 'Active';
};

const STATUS_COLOR: Record<CustomPriceListStatus, string> = {
  Archived: 'failure',
  Draft: 'neutral',
  Expired: 'warning',
  Active: 'success',
};

const OP_TYPE_COLOR: Record<string, string> = {
  Purchase: 'warning',
  Sale: 'success',
  Brokerage: 'primary',
};

//
// ⚛️ ⚛️ ⚛️ CUSTOM PRICE LIST DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const CustomPriceListDetailView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useLocation();

  // States
  const [currentTab, setCurrentTab] =
    React.useState<CustomPriceListTab>('pricings');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isImportFromOpen, setIsImportFromOpen] = React.useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = React.useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [editNotes, setEditNotes] = React.useState('');

  // Atoms
  const customPriceListId = useAtomValue(state.$customPriceListId);
  const {
    data: listData,
    isFetching: listFetching,
    refetch,
  } = useAtomValue(state.$remoteCustomPriceList);
  const { data: itemPricings, refetch: refetchItemPricings } = useAtomValue(
    state.$remoteProductPricings,
  );
  const isLoading = useAtomValue(state.$isCustomPriceListLoading);
  const error = useAtomValue(state.$customPriceListError);
  const { refetch: refetchEvaluatedFormulas } = useAtomValue(
    state.$remoteEvaluatedFormulas,
  );
  const publishAction = useSetAtom(state.$publishCustomPriceList);
  const archiveAction = useSetAtom(state.$archiveCustomPriceList);
  const unarchiveAction = useSetAtom(state.$unarchiveCustomPriceList);
  const deleteAction = useSetAtom(state.$deleteCustomPriceList);
  const updateAction = useSetAtom(customPriceListUpdateAtom({}));
  const generateDocumentAction = useSetAtom(generatePriceListDocumentAtom({}));

  // Data hooks
  const toast = useToast();
  const { downloadFile } = useFileDownload();
  const handleDuplicateSuccess = React.useCallback(
    (newId: string) => navigate(`/pricing/lists/${newId}`),
    [navigate],
  );
  const duplicate = useDuplicateCustomPriceList(handleDuplicateSuccess);

  const customPriceList = listData?.customPriceLists?.[0];

  // Memoized values
  const pricingsList = React.useMemo(() => itemPricings ?? [], [itemPricings]);

  const itemNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const ip of pricingsList) {
      map.set(ip.item.id, ip.item.name);
    }
    return map;
  }, [pricingsList]);

  const status = React.useMemo(
    () => (customPriceList ? computeStatus(customPriceList) : null),
    [customPriceList],
  );

  const isArchived = status === 'Archived';

  const isUpdatedAtRecent = React.useMemo(
    () => isDateWithinHours(customPriceList?.timestamps.updatedAt, 12),
    [customPriceList],
  );

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(pricingRoutePaths.customPriceLists);
  }, [navigate]);

  const handlePublish = React.useCallback(async () => {
    if (!customPriceListId) return;
    try {
      await publishAction({ id: customPriceListId });
      toast.success(t.pricing.customPriceLists.toast.published);
      refetch?.();
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.publishError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [customPriceListId, publishAction, toast, t, refetch]);

  const handleArchive = React.useCallback(async () => {
    if (!customPriceListId) return;
    try {
      await archiveAction({ id: customPriceListId });
      toast.success(t.pricing.customPriceLists.toast.archived);
      refetch?.();
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.archiveError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [customPriceListId, archiveAction, toast, t, refetch]);

  const handleUnarchive = React.useCallback(async () => {
    if (!customPriceListId) return;
    try {
      await unarchiveAction({ id: customPriceListId });
      toast.success(t.pricing.customPriceLists.toast.unarchived);
      refetch?.();
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.archiveError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [customPriceListId, unarchiveAction, toast, t, refetch]);

  const handleDelete = React.useCallback(async () => {
    if (!customPriceListId) return;
    setIsDeleting(true);
    try {
      await deleteAction({ id: customPriceListId });
      toast.success(t.pricing.customPriceLists.toast.deleted);
      navigate(pricingRoutePaths.customPriceLists);
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
    }
  }, [customPriceListId, deleteAction, toast, t, navigate]);

  const handleDuplicate = React.useCallback(() => {
    if (!customPriceList || !customPriceListId) return;
    duplicate.open(customPriceListId, customPriceList.name);
  }, [customPriceList, customPriceListId, duplicate]);

  const handleOpenEdit = React.useCallback(() => {
    if (!customPriceList) return;
    setEditName(customPriceList.name);
    setEditNotes(customPriceList.notes ?? '');
    setIsEditDialogOpen(true);
  }, [customPriceList]);

  const handleSaveEdit = React.useCallback(async () => {
    if (!customPriceListId || !editName.trim()) return;
    try {
      await updateAction({
        id: customPriceListId,
        name: editName.trim(),
        notes: editNotes.trim() || null,
      });
      toast.success(t.pricing.customPriceLists.toast.updated);
      setIsEditDialogOpen(false);
      refetch?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [customPriceListId, editName, editNotes, updateAction, toast, t, refetch]);

  const handleDownloadPdf = React.useCallback(async () => {
    if (!customPriceListId) return;
    try {
      const result = await generateDocumentAction({
        priceListId: customPriceListId,
        priceListType: 'custom',
      });
      const fileId = result?.pricingGeneratePriceListDocument?.fileId;
      if (fileId) {
        await downloadFile({
          id: fileId,
          fileName: `${customPriceList?.name ?? 'price-list'}.pdf`,
        });
      }
      toast.success(t.pricing.customPriceLists.toast.pdfGenerated);
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.pdfError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [
    customPriceListId,
    customPriceList?.name,
    generateDocumentAction,
    downloadFile,
    toast,
    t,
  ]);

  const badges = React.useMemo(() => {
    if (!customPriceList || !status) return null;
    return (
      <div className="flex gap-2">
        <Badge
          color={
            (OP_TYPE_COLOR[customPriceList.operationType] ?? 'neutral') as
              | 'warning'
              | 'success'
              | 'primary'
              | 'neutral'
          }
          size="small"
        >
          {customPriceList.operationType}
        </Badge>
        {customPriceList.pricingMode === 'CRV' && (
          <Badge color="primary" size="small">
            {t.pricing.customPriceLists.create.fields.pricingModeCRV}
          </Badge>
        )}
        <Badge
          color={
            STATUS_COLOR[status] as
              | 'failure'
              | 'neutral'
              | 'warning'
              | 'success'
          }
          size="small"
        >
          {
            t.pricing.customPriceLists.status[
              status.toLowerCase() as keyof typeof t.pricing.customPriceLists.status
            ]
          }
        </Badge>
      </div>
    );
  }, [customPriceList, status, t]);

  const menuItems = React.useMemo(() => {
    if (!status) return [];
    const items: MenuItem[] = [];
    if (!isArchived) {
      items.push({
        label: t.pricing.customPriceLists.detail.actions.edit,
        onAction: handleOpenEdit,
        icon: <LucidePencil size={16} />,
      });
      items.push('separator');
    }
    if (status === 'Archived') {
      items.push({
        label: t.pricing.customPriceLists.detail.actions.unarchive,
        onAction: handleUnarchive,
        icon: <LucideArchiveRestore size={16} />,
      });
    }
    if (status === 'Draft') {
      items.push({
        label: t.pricing.customPriceLists.detail.actions.importFrom,
        onAction: () => setIsImportFromOpen(true),
        icon: <LucideDownload size={16} />,
      });
      items.push('separator');
    }
    items.push({
      label: t.pricing.customPriceLists.detail.actions.viewHistory,
      onAction: () => setIsHistoryOpen(true),
      icon: <LucideHistory size={16} />,
    });
    items.push({
      label: t.pricing.customPriceLists.detail.actions.downloadPdf,
      onAction: handleDownloadPdf,
      icon: <LucideFileDown size={16} />,
    });
    items.push({
      label: t.pricing.customPriceLists.detail.actions.sendEmail,
      onAction: () => setIsEmailDialogOpen(true),
      icon: <LucideMail size={16} />,
    });
    items.push('separator');
    items.push({
      label: t.pricing.customPriceLists.detail.actions.duplicate,
      onAction: handleDuplicate,
      icon: <LucideFiles size={16} />,
    });
    if (!customPriceList?.isPublished) {
      items.push('separator');
      items.push({
        label: t.pricing.customPriceLists.detail.actions.delete,
        onAction: () => setIsDeleteConfirmOpen(true),
        icon: <LucideTrash2 size={16} />,
        className: 'text-failure',
      });
    }
    return items;
  }, [
    status,
    customPriceList,
    isArchived,
    handleArchive,
    handleUnarchive,
    handleDuplicate,
    handleDownloadPdf,
    handleOpenEdit,
    t,
  ]);

  const headerActions = React.useMemo(() => {
    if (!status) return null;
    return (
      <div className="flex items-center gap-2">
        {status === 'Draft' && (
          <Button
            variant="solid"
            color="primary"
            icon={<LucideGlobe size={16} />}
            onPress={handlePublish}
          >
            {t.pricing.customPriceLists.detail.actions.publish}
          </Button>
        )}
        {(status === 'Draft' || status === 'Active') && (
          <Button
            variant="outline"
            color="failure"
            icon={<LucideArchive size={16} />}
            onPress={handleArchive}
          >
            {t.pricing.customPriceLists.detail.actions.archive}
          </Button>
        )}
        <Menu
          trigger={
            <Button
              variant="outline"
              color="neutral"
              icon={<LucideEllipsis size={16} />}
            />
          }
          items={menuItems}
        />
      </div>
    );
  }, [status, menuItems, handlePublish, handleArchive, t]);

  const pricingsTabContent = React.useCallback(
    () => (
      <CustomPriceListPricingsTab
        customPriceListId={customPriceListId ?? ''}
        isArchived={isArchived}
        isActive={status === 'Active'}
        pricingMode={customPriceList?.pricingMode}
        startDate={customPriceList?.startDate ?? null}
      />
    ),
    [
      customPriceListId,
      customPriceList?.pricingMode,
      customPriceList?.startDate,
      isArchived,
      status,
    ],
  );

  if (isLoading || listFetching) {
    return (
      <div className="@CustomPriceListDetailView flex h-full flex-col">
        <div className="space-y-4 p-6">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !customPriceList) {
    return (
      <div className="@CustomPriceListDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.common.errors.notFound(t.pricing.customPriceLists.detail.title)}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          {t.pricing.customPriceLists.detail.backButton}
        </Button>
      </div>
    );
  }

  return (
    <div className="@CustomPriceListDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as CustomPriceListTab)}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={customPriceList.name}
        subtitle={
          <Row gap1 className="flex-wrap">
            {t.pricing.customPriceLists.detail.subtitle.created}{' '}
            <LocalizedDate date={customPriceList.timestamps.createdAt} />
            {customPriceList.startDate && (
              <>
                &bull; {t.pricing.customPriceLists.detail.subtitle.startsOn}{' '}
                <LocalizedDate
                  date={customPriceList.startDate}
                  dateStyle="medium"
                />
                {customPriceList.endDate && (
                  <>
                    <span className="mx-1">&ndash;</span>
                    <LocalizedDate
                      date={customPriceList.endDate}
                      dateStyle="medium"
                    />
                  </>
                )}
              </>
            )}
            {customPriceList.notes && (
              <span className="ml-2 text-neutral/60">
                &bull; {customPriceList.notes}
              </span>
            )}
            {customPriceList.updatedByUser && (
              <>
                &bull;{' '}
                {t.pricing.customPriceLists.detail.subtitle.lastUpdatedBy}{' '}
                {customPriceList.updatedByUser.name}{' '}
                {t.pricing.customPriceLists.detail.subtitle.on}{' '}
                <LocalizedDate
                  date={customPriceList.timestamps.updatedAt}
                  dateStyle="short"
                />
                {isUpdatedAtRecent && (
                  <LocalizedTime
                    time={customPriceList.timestamps.updatedAt}
                    timeStyle="short"
                  />
                )}
              </>
            )}
          </Row>
        }
        badges={badges}
        headerActions={headerActions}
        tabs={{
          pricings: {
            title: t.pricing.customPriceLists.detail.tabs.pricings,
            content: pricingsTabContent,
          },
        }}
      />

      {customPriceListId && (
        <>
          <ImportFromPriceListDialog
            isOpen={isImportFromOpen}
            onClose={() => setIsImportFromOpen(false)}
            targetCustomPriceListId={customPriceListId}
            onSuccess={() => {
              refetchItemPricings();
              refetchEvaluatedFormulas();
            }}
          />
          <PriceListEmailDialog
            isOpen={isEmailDialogOpen}
            onClose={() => setIsEmailDialogOpen(false)}
            priceListId={customPriceListId}
            priceListType="custom"
            priceListName={customPriceList?.name ?? ''}
          />
          <PriceListHistoryDialog
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            priceListId={customPriceListId}
            priceListName={customPriceList?.name ?? ''}
            itemNameMap={itemNameMap}
          />
          <DuplicateCustomPriceListDialog duplicate={duplicate} />
        </>
      )}

      <ConsoleDialog
        className="@CustomPriceListEditDialog w-full"
        isOpen={isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) setIsEditDialogOpen(false);
        }}
        title={t.pricing.customPriceLists.detail.actions.edit}
        content={
          <div className="grid gap-4">
            <TextField
              label={t.pricing.customPriceLists.create.fields.name}
              value={editName}
              onChange={setEditName}
              isRequired
            />
            <TextField
              label={t.pricing.customPriceLists.create.fields.notes}
              value={editNotes}
              onChange={setEditNotes}
            />
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              color="neutral"
              onPress={() => setIsEditDialogOpen(false)}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSaveEdit}
              isDisabled={!editName.trim()}
            >
              {t.common.actions.save}
            </Button>
          </div>
        }
      />

      <ConsoleDialog
        className="@CustomPriceListDeleteConfirm"
        isOpen={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setIsDeleteConfirmOpen(false);
        }}
        title={t.pricing.customPriceLists.detail.actions.confirmDelete.title}
        content={
          <p className="text-sm text-neutral/70">
            {
              t.pricing.customPriceLists.detail.actions.confirmDelete
                .description
            }
          </p>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              color="neutral"
              onPress={() => setIsDeleteConfirmOpen(false)}
              isDisabled={isDeleting}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              icon={<LucideTrash2 size={16} />}
              onPress={handleDelete}
              isDisabled={isDeleting}
            >
              {t.pricing.customPriceLists.detail.actions.confirmDelete.confirm}
            </Button>
          </div>
        }
      />
    </div>
  );
};

type PricingsTabProps = {
  customPriceListId: string;
  isArchived: boolean;
  isActive: boolean;
  pricingMode?: string;
  startDate: CustomPriceListRecord['startDate'];
};

//
// ⚛️ ⚛️ ⚛️ CUSTOM PRICE LIST PRICINGS TAB ⚛️ ⚛️ ⚛️
//
const CustomPriceListPricingsTab = ({
  customPriceListId,
  isArchived,
  isActive,
  pricingMode,
  startDate,
}: PricingsTabProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const {
    data: itemPricings,
    isFetching: pricingsFetching,
    refetch: refetchItemPricings,
  } = useAtomValue(state.$remoteProductPricings);
  const { refetch: refetchEvaluatedFormulas } = useAtomValue(
    state.$remoteEvaluatedFormulas,
  );
  const evaluatedPrices = useAtomValue(state.$evaluatedPrices);
  const variables = useAtomValue(variablesState.$allVariablesForAutocomplete);
  const [selectedCell, setSelectedCell] = useAtom(state.$selectedCell);
  const [asOfDateTime, setAsOfDateTime] = useAtom(state.$asOfDateTime);
  const addItemPricing = useSetAtom(customPriceListAddItemPricingAtom({}));
  const deleteItemPricing = useSetAtom(
    customPriceListDeleteItemPricingAtom({}),
  );
  const batchUpdateItemPricings = useSetAtom(
    customPriceListBatchUpdateItemPricingsAtom({}),
  );
  const reorderItemPricings = useSetAtom(
    customPriceListReorderItemPricingsAtom({}),
  );

  const isDraft = !isArchived && !isActive;

  // Memoized values
  const pricingsList = React.useMemo(() => itemPricings ?? [], [itemPricings]);

  // Callbacks
  const handleBatchSave = React.useCallback(
    async (params: { listId: string; items: BatchSaveItem[] }) => {
      await batchUpdateItemPricings({
        customPriceListId: params.listId,
        items: params.items,
      });
    },
    [batchUpdateItemPricings],
  );

  const handleAddItemPricing = React.useCallback(
    async (input: AddItemPricingInput) => {
      if (!customPriceListId) return;
      await addItemPricing({
        customPriceListId,
        itemId: input.itemId,
        pricingUnit: input.pricingUnit,
        position: input.position,
        tiers: input.tiers,
      });
    },
    [customPriceListId, addItemPricing],
  );

  const handleReorderItemPricings = React.useCallback(
    async (params: { itemPricingIds: string[] }) => {
      if (!customPriceListId) return;
      await reorderItemPricings({
        customPriceListId,
        itemPricingIds: params.itemPricingIds,
      });
    },
    [customPriceListId, reorderItemPricings],
  );

  // Effects
  React.useEffect(() => {
    setAsOfDateTime(isDraft ? null : startDate);
  }, [isDraft, startDate, setAsOfDateTime]);

  return (
    <div className="@CustomPriceListPricingsTab h-full">
      <PriceListView
        priceListId={customPriceListId}
        itemPricings={pricingsList}
        pricingsFetching={pricingsFetching}
        variables={variables}
        evaluatedPrices={evaluatedPrices}
        selectedCell={selectedCell}
        setSelectedCell={setSelectedCell}
        onBatchSave={handleBatchSave}
        onAddItemPricing={handleAddItemPricing}
        onDeleteItemPricing={deleteItemPricing}
        onReorderItemPricings={
          isArchived ? undefined : handleReorderItemPricings
        }
        onRefetchPricings={refetchItemPricings}
        onRefetchEvaluated={refetchEvaluatedFormulas}
        isReadOnly={isArchived}
        pricingMode={pricingMode as 'Standard' | 'CRV'}
        asOfDateTime={asOfDateTime}
        onAsOfDateTimeChange={setAsOfDateTime}
        isAsOfReadOnly={isActive}
        emptyTitle={t.pricing.customPriceLists.detail.pricings.empty.title}
        emptyDescription={
          t.pricing.customPriceLists.detail.pricings.empty.description
        }
        gridAriaLabel={t.pricing.customPriceLists.detail.tabs.pricings}
      />
    </div>
  );
};
